/**
 * Armado del texto listo para publicar en Instagram u otras redes.
 *
 * Todas las funciones de este módulo son puras: el componente resuelve el store
 * y les pasa los datos ya resueltos. Así la lógica se puede probar y reusar sin
 * arrastrar redux.
 */

import { formatNumber, formatPrice } from './formatters';
import { htmlToPlainText, truncateText } from './htmlUtil';
import moment from 'moment';
import 'moment/locale/es';

//Grupos de atributos que se muestran en la card del listado
export const DEFAULT_ATTRIBUTE_GROUPS = [
  'basic',
  'surfaces',
  'antiquity',
  'expenses',
  'main'
];

/**
 * Dirección publicable de la propiedad.
 *
 * Cuando la propiedad tiene activado "No mostrar la dirección exacta" NUNCA se
 * devuelve la dirección real: se usa la ficticia y, si está vacía, se devuelve
 * un string vacío para que el caption caiga a barrio/ciudad. El piso y el
 * departamento tampoco se agregan en ese caso, porque anularían el anonimato.
 *
 * La regla vive acá adentro a propósito, para que ningún caller la pueda
 * saltear por olvido.
 *
 * @param {?Object} property
 * @returns {string}
 */
export function buildDisplayAddress(property) {
  if (!property) return '';

  if (property.isFakeAddress)
    return property.fake_address ? String(property.fake_address).trim() : '';

  const address = property.address ? String(property.address).trim() : '';
  if (!address) return '';

  return [address, property.floor, property.apartment]
    .filter(part => part !== null && part !== undefined && part !== '')
    .join(' ')
    .trim();
}

/**
 * Línea de ubicación: dirección publicable + barrio + ciudad + provincia, sin
 * vacíos ni repetidos (es habitual que barrio y ciudad sean el mismo nombre).
 *
 * @param {?Object} property
 * @returns {string}
 */
export function buildLocationLine(property) {
  if (!property) return '';

  const parts = [
    buildDisplayAddress(property),
    property.neighborhood,
    property.city,
    property.state
  ];

  const result = [];
  parts.forEach(part => {
    const value = part ? String(part).trim() : '';
    if (!value) return;
    const alreadyThere = result.some(
      existing => existing.toLowerCase() === value.toLowerCase()
    );
    if (!alreadyThere) result.push(value);
  });

  return result.join(', ');
}

/**
 * Devuelve la versión vigente de una entidad: lo que está en edición pisa a lo
 * recién creado y ambos pisan a lo que vino del backend. Es la misma
 * precedencia que usan PropertyCard y PropertyAttributeChips.
 */
const resolveEntity = (slice, collectionName, id) => {
  if (!slice) return null;
  if (slice.update?.activos && slice.update.activos.indexOf(id) !== -1)
    return slice.update.activo[id];
  if (slice.create?.nuevos && slice.create.nuevos.indexOf(id) !== -1)
    return slice.create.nuevo[id];
  return slice.byId?.[collectionName]?.[id] || null;
};

/**
 * Operaciones publicables de la propiedad (las que tienen moneda y precio).
 *
 * @param {?Object} property
 * @param {Object} slices { propertyOperations, operationTypes }
 * @returns {Array<{name: string, price: *, currency: string}>}
 */
export function resolveOperations(property, slices) {
  const { propertyOperations, operationTypes } = slices || {};
  if (!property?.property_operation) return [];

  const operations = [];
  property.property_operation.forEach(idPropertyOperation => {
    const propertyOperation = resolveEntity(
      propertyOperations,
      'propertyOperations',
      idPropertyOperation
    );
    if (!propertyOperation) return;

    const operationType =
      operationTypes?.byId?.operationTypes?.[propertyOperation.idOperationType];
    if (!operationType) return;
    if (!propertyOperation.codCurrency || !propertyOperation.price) return;

    operations.push({
      name: operationType.nameType || '',
      price: propertyOperation.price,
      currency: propertyOperation.codCurrency
    });
  });

  return operations;
}

/**
 * Atributos de la propiedad ya formateados para mostrar.
 *
 * Aplica las mismas reglas que PropertyAttributeChips (enums, moneda, fechas,
 * los casos especiales de antigüedad y el sufijo de unidad de las superficies),
 * pero en dos pasadas: primero busca la unidad de medida y después arma los
 * valores. La versión de los chips lo hace en una sola pasada guardando la
 * unidad en un useState durante el render, y por eso pierde el sufijo en el
 * primer render cuando surface_measurement aparece después de una superficie.
 *
 * @param {?Object} property
 * @param {Object} slices
 * @param {Array<string>} [groups] códigos de grupo a incluir
 * @returns {Array<{cod: string, group: string, type: string, name: string, value: string, rawValue: *}>}
 */
export function resolveAttributes(property, slices, groups) {
  const {
    attributes,
    attributesGroups,
    attributesValues,
    propertiesAttributesValues
  } = slices || {};
  if (!property?.property_attribute_value) return [];

  const allowedGroups = groups || DEFAULT_ATTRIBUTE_GROUPS;

  //Pares (atributo, valor) vigentes, descartando lo que no se puede resolver
  const pairs = [];
  property.property_attribute_value.forEach(idPropertyAttributeValue => {
    const propertyAttributeValue = resolveEntity(
      propertiesAttributesValues,
      'propertiesAttributesValues',
      idPropertyAttributeValue
    );
    if (!propertyAttributeValue) return;

    const attribute =
      attributes?.byId?.attributes?.[propertyAttributeValue.idAttribute];
    if (!attribute) return;

    pairs.push({ attribute, propertyAttributeValue });
  });

  //Primera pasada: la unidad de medida de las superficies (m2, ha, etc.)
  let surfaceUnit = '';
  pairs.some(({ attribute, propertyAttributeValue }) => {
    if (attribute.codAttribute !== 'surface_measurement') return false;
    surfaceUnit = propertyAttributeValue.value || '';
    return true;
  });

  //Segunda pasada: formateo
  const resolved = [];
  pairs.forEach(({ attribute, propertyAttributeValue }) => {
    if (attribute.codAttribute === 'surface_measurement') return;

    const rawValue = propertyAttributeValue.value;
    if (rawValue === null || rawValue === undefined || rawValue === '') return;

    const attributeGroup =
      attributesGroups?.byId?.attributesGroups?.[attribute.idAttributeGroup];
    const groupCod = attributeGroup?.codAttributeGroup || '';
    if (allowedGroups.indexOf(groupCod) === -1) return;

    let value = rawValue;

    //Si el enum no matchea dejamos el valor crudo antes que perder la fila
    if (attribute.typeAttribute === 'enum' && attribute.attribute_value) {
      attribute.attribute_value.some(idAttributeValue => {
        const attributeValue =
          attributesValues?.byId?.attributesValues?.[idAttributeValue];
        if (attributeValue && attributeValue.codAttributeValue === value) {
          value = attributeValue.nameAttributeValue;
          return true;
        }
        return false;
      });
    }

    if (groupCod === 'surfaces') value = `${value}${surfaceUnit}`;

    if (attribute.typeAttribute === 'currency') value = formatPrice(value, '$');

    if (attribute.typeAttribute === 'date')
      value = value ? moment(value).format('MMMM YYYY') : '';

    if (attribute.codAttribute === 'age') {
      if (rawValue === '0' || rawValue === 0) value = 'A estrenar';
      else if (rawValue === '-1' || rawValue === -1) value = 'En construcción';
      else if (parseInt(rawValue, 10) > 0) value = `${rawValue} años`;
    }

    if (value === null || value === undefined || value === '') return;

    resolved.push({
      cod: attribute.codAttribute || '',
      group: groupCod,
      type: attribute.typeAttribute || '',
      name: attribute.nameAttribute || '',
      value: String(value),
      rawValue: rawValue
    });
  });

  return resolved;
}

/**
 * Pasa el nombre del atributo a minúscula y lo pone en singular cuando el valor
 * es 1. Alcanza con sacar la "s" final para todo el vocabulario del dominio:
 * dormitorios, baños, cocheras, ambientes, plantas.
 */
const pluralizeLabel = (name, value) => {
  const label = String(name || '').toLowerCase();
  return Number(value) === 1 && label.endsWith('s')
    ? label.slice(0, -1)
    : label;
};

const isTruthyValue = value =>
  value !== false &&
  value !== 0 &&
  value !== '0' &&
  String(value).toLowerCase() !== 'false';

/**
 * Una línea de característica. Los valores numéricos quedan como "3 dormitorios"
 * y el resto como "Estado: Bueno".
 */
const buildAttributeLine = attribute => {
  if (attribute.type === 'boolean')
    return isTruthyValue(attribute.rawValue) ? `▫️ ${attribute.name}` : null;

  if (attribute.type === 'number' && attribute.group !== 'surfaces')
    return `▫️ ${formatNumber(attribute.rawValue)} ${pluralizeLabel(
      attribute.name,
      attribute.rawValue
    )}`;

  return `▫️ ${attribute.name}: ${attribute.value}`;
};

/**
 * Arma el texto listo para pegar en Instagram o cualquier otra red.
 *
 * Los bloques que quedan vacíos se omiten enteros, para no dejar un emoji
 * colgado sin contenido.
 *
 * @param {Object} input
 * @param {Object} input.property objeto property del store
 * @param {?string} input.propertyTypeName
 * @param {?string} input.propertySubTypeName
 * @param {Array} input.operations salida de resolveOperations()
 * @param {Array} input.attributes salida de resolveAttributes()
 * @param {?string} input.publicUrl link público, o null si el tenant no publicó su web
 * @param {Object} [input.options]
 * @param {boolean} [input.options.withDescription=true]
 * @param {number} [input.options.maxDescriptionLength=700]
 * @returns {string}
 */
export function buildPropertyCaption(input) {
  const {
    property,
    propertyTypeName,
    propertySubTypeName,
    operations = [],
    attributes = [],
    publicUrl,
    options = {}
  } = input || {};

  if (!property) return '';

  const { withDescription = true, maxDescriptionLength = 700 } = options;

  const blocks = [];

  //Título
  const title = property.publication_title
    ? String(property.publication_title).trim()
    : '';
  if (title) {
    blocks.push(`🏡 ${title}`);
  } else {
    const type = [propertyTypeName, propertySubTypeName]
      .filter(Boolean)
      .join(' - ');
    const operationNames = operations.map(o => o.name).filter(Boolean);
    const heading = [type, operationNames.join(' / ')]
      .filter(Boolean)
      .join(' en ');
    if (heading) blocks.push(`🏡 ${heading}`);
  }

  //Ubicación
  const location = buildLocationLine(property);
  if (location) blocks.push(`📍 ${location}`);

  //Precios
  const priceLines = operations.map(
    o => `💰 ${o.name}: ${formatPrice(o.price, o.currency)}`
  );
  if (priceLines.length > 0) blocks.push(priceLines.join('\n'));

  //Características
  const attributeLines = attributes.map(buildAttributeLine).filter(Boolean);
  if (attributeLines.length > 0) blocks.push(attributeLines.join('\n'));

  //Descripción
  if (withDescription) {
    const description = truncateText(
      htmlToPlainText(property.rich_desProperty || property.desProperty),
      maxDescriptionLength
    );
    if (description) blocks.push(description);
  }

  //Link
  if (publicUrl) blocks.push(`🔗 Más información: ${publicUrl}`);

  return blocks.join('\n\n');
}

/**
 * Slug apto para nombre de archivo: sin acentos, sin símbolos y separado por
 * guiones.
 *
 * @param {?string} text
 * @returns {string}
 */
export function slugify(text) {
  if (!text) return '';
  return String(text)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default {
  buildDisplayAddress,
  buildLocationLine,
  resolveOperations,
  resolveAttributes,
  buildPropertyCaption,
  slugify
};
